import * as React from 'react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    MenuItem,
    Stack,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography
} from '@mui/material';
import { useNotify } from 'react-admin';
import ScheduleSendIcon from '@mui/icons-material/ScheduleSend';
import PushService, { type PushCampaign, type PushQueue, type QueueRow, type QueueStatus } from '../../services/push/pushService.ts';
import { errorText, fmtAgo } from '../attractions/attractionsShared.tsx';

const pushService = new PushService();

const STATUS_LABELS: Record<QueueStatus, string> = {
    pending: 'Waiting',
    sent: 'Sent',
    cancelled: 'Dropped',
    all: 'All'
};

const DAY_OPTIONS = [1, 7, 30, 90];

/** Plain words for a cancel reason, so the page explains itself. */
const REASON_TEXT: Record<string, string> = {
    CUSTOMER_ORDERED: 'ordered',
    VENUE_CLOSED: 'restaurant closed',
    VENUE_OUT_OF_AREA: 'restaurant out of area',
    DAILY_CAP: 'one push a day',
    OPTED_OUT: 'opted out',
    EXPIRED: 'too late',
    NO_DEVICE_TOKEN: 'no phone to send to',
    CAMPAIGN_INACTIVE: 'campaign off',
    RECENT_ORDER: 'just ordered',
    QUIET_HOURS: 'quiet hours',
    NO_RESTAURANT: 'no restaurant',
    NO_CHANNEL: 'no channel',
    TRIP_ENDED: 'left Cyprus',
    CART_EMPTIED: 'cart emptied',
    STAGE_ADVANCED: 'moved on',
    PAID: 'paid',
    WRONG_SEGMENT: 'resident, not a visitor'
};

const reasonText = (r: string | null): string => (r ? REASON_TEXT[r] ?? r.toLowerCase().replace(/_/g, ' ') : '');

/** Local date and time, the year only when it is not this one. */
const fmtWhen = (iso: string | null | undefined): string => {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    const sameYear = d.getFullYear() === new Date().getFullYear();
    return d.toLocaleString('en-GB', {
        day: 'numeric', month: 'short', ...(sameYear ? {} : { year: 'numeric' }), hour: '2-digit', minute: '2-digit'
    });
};

/** "in 10 min", "in 2 h", "in 3 days" for something still to come; otherwise how long ago. */
const fmtDue = (iso: string, now = new Date()): string => {
    const t = new Date(iso).getTime();
    if (Number.isNaN(t)) return '';
    const mins = Math.round((t - now.getTime()) / 60000);
    if (mins < 0) return fmtAgo(iso, now);
    if (mins < 1) return 'now';
    if (mins < 60) return `in ${mins} min`;
    const hours = Math.round(mins / 60);
    if (hours < 36) return `in ${hours} h`;
    return `in ${Math.round(hours / 24)} days`;
};

const StatusChip = ({ row }: { row: QueueRow }) => {
    if (row.status === 'SENT') {
        return <Chip size="small" color="success" variant="outlined" label={row.tappedAt ? 'Sent · tapped' : 'Sent'} />;
    }
    if (row.status === 'CANCELLED') {
        return <Chip size="small" variant="outlined" label={`Dropped: ${reasonText(row.cancelReason)}`} />;
    }
    return <Chip size="small" color="primary" variant="outlined" label="Waiting" />;
};

/**
 * The push queue (Ariel, 28 Oct 2026): every scheduled push, who it is for,
 * when it goes and what became of it. The server builds the rows; this page
 * only picks the status, the window and a campaign, and searches.
 */
const PushQueuePage = () => {
    const notify = useNotify();
    const [status, setStatus] = useState<QueueStatus>('pending');
    const [days, setDays] = useState(7);
    const [campaignId, setCampaignId] = useState<number | ''>('');
    const [campaigns, setCampaigns] = useState<PushCampaign[]>([]);
    const [data, setData] = useState<PushQueue | null>(null);
    const [loading, setLoading] = useState(true);
    const [failed, setFailed] = useState<string | null>(null);
    const [search, setSearch] = useState('');

    const load = useCallback(async () => {
        setLoading(true);
        setFailed(null);
        try {
            setData(await pushService.queue(status, days, campaignId === '' ? undefined : campaignId));
        } catch (e) {
            setData(null);
            setFailed(errorText(e, 'Could not load the push queue. Reload the page to try again.'));
            notify('Could not load the push queue', { type: 'warning' });
        } finally {
            setLoading(false);
        }
    }, [status, days, campaignId, notify]);

    useEffect(() => {
        void load();
    }, [load]);

    useEffect(() => {
        pushService.list().then(setCampaigns).catch(() => setCampaigns([]));
    }, []);

    const rows = useMemo(() => {
        const q = search.trim().toLowerCase();
        const all = data?.rows ?? [];
        if (!q) return all;
        return all.filter((r) => [r.customerName, r.phone, r.campaignName, r.campaignKey, r.restaurant, r.cancelReason]
            .some((v) => (v ?? '').toLowerCase().includes(q)));
    }, [data, search]);

    const windowText = status === 'pending' ? 'everything still to go' : `the last ${days === 1 ? '24 hours' : `${days} days`}`;

    return (
        <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1200 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
                <ScheduleSendIcon color="primary" />
                <Typography variant="h5">Push queue</Typography>
            </Stack>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3, maxWidth: 720 }}>
                Every push the campaigns have scheduled: who it is for, when it goes, and what became of it.
                Waiting pushes are checked again at the moment they are due, so a row can still be dropped
                (restaurant closed, one push a day, just ordered). Times are your local time.
            </Typography>

            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} useFlexGap flexWrap="wrap">
                        <ToggleButtonGroup
                            size="small"
                            exclusive
                            value={status}
                            onChange={(_e, v: QueueStatus | null) => v && setStatus(v)}
                            aria-label="status"
                        >
                            {(Object.keys(STATUS_LABELS) as QueueStatus[]).map((s) => (
                                <ToggleButton key={s} value={s}>{STATUS_LABELS[s]}</ToggleButton>
                            ))}
                        </ToggleButtonGroup>
                        <ToggleButtonGroup
                            size="small"
                            exclusive
                            value={days}
                            onChange={(_e, v: number | null) => v && setDays(v)}
                            aria-label="window"
                            disabled={status === 'pending'}
                        >
                            {DAY_OPTIONS.map((d) => (
                                <ToggleButton key={d} value={d}>{d === 1 ? '24 h' : `${d} days`}</ToggleButton>
                            ))}
                        </ToggleButtonGroup>
                        <TextField
                            select
                            size="small"
                            label="Campaign"
                            value={campaignId}
                            onChange={(e) => setCampaignId(e.target.value === '' ? '' : Number(e.target.value))}
                            sx={{ minWidth: 220 }}
                        >
                            <MenuItem value="">All campaigns</MenuItem>
                            {campaigns.map((c) => (
                                <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                            ))}
                        </TextField>
                        <TextField
                            size="small"
                            label="Search"
                            placeholder="name, phone, restaurant"
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            sx={{ minWidth: 220 }}
                        />
                    </Stack>
                    {data && (
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                            {data.pending.toLocaleString('en-GB')} waiting ·{' '}
                            {data.sent.toLocaleString('en-GB')} sent and {data.cancelled.toLocaleString('en-GB')} dropped in {windowText}
                            {' · '}as of {fmtWhen(data.generatedAt)}
                        </Typography>
                    )}
                </CardContent>
            </Card>

            {failed && <Alert severity="error" sx={{ mb: 3 }}>{failed}</Alert>}

            <Card>
                <CardContent sx={{ overflowX: 'auto' }}>
                    {loading && !data ? (
                        <Stack alignItems="center" sx={{ py: 6 }}><CircularProgress /></Stack>
                    ) : rows.length === 0 ? (
                        <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center' }}>
                            {search.trim() ? 'Nothing matches that search.' : `Nothing ${status === 'pending' ? 'waiting' : status === 'sent' ? 'sent' : status === 'cancelled' ? 'dropped' : 'here'} ${status === 'pending' ? '' : `in ${windowText}`}.`}
                        </Typography>
                    ) : (
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>{status === 'pending' ? 'Goes' : 'When'}</TableCell>
                                    <TableCell>Campaign</TableCell>
                                    <TableCell>Who</TableCell>
                                    <TableCell>Restaurant</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell>Notes</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {rows.map((r) => {
                                    const when = r.status === 'SENT' ? r.sentAt : r.status === 'CANCELLED' ? r.cancelledAt : r.dueAt;
                                    const notes: string[] = [];
                                    if (r.cartItems != null) notes.push(`${r.cartItems} item${r.cartItems === 1 ? '' : 's'} in the cart`);
                                    if (r.triggerOrderId != null) notes.push(`order ${r.triggerOrderId}`);
                                    if (r.attributedOrderId != null) notes.push(`led to order ${r.attributedOrderId}`);
                                    if (r.creditGranted) notes.push('credit granted');
                                    if (r.tapAction) notes.push(`opened ${r.tapAction.toLowerCase()}`);
                                    if (r.channel && r.channel !== 'PUSH') notes.push(r.channel.toLowerCase());
                                    if (r.status === 'PENDING' && r.tripId != null) notes.push('visit');
                                    return (
                                        <TableRow key={r.id} hover>
                                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                                <Tooltip title={fmtWhen(when)}>
                                                    <span>{r.status === 'PENDING' ? fmtDue(r.dueAt) : fmtAgo(when)}</span>
                                                </Tooltip>
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    {fmtWhen(when)}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>
                                                {r.campaignName}
                                                <Typography variant="caption" color="text.secondary" display="block">{r.campaignKey}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                {r.customerName?.trim() || (r.deviceId != null ? `Guest phone ${r.deviceId}` : '—')}
                                                <Typography variant="caption" color="text.secondary" display="block">
                                                    {r.phone ?? ''}{r.country ? ` · ${r.country}` : ''}
                                                </Typography>
                                            </TableCell>
                                            <TableCell>{r.restaurant ?? '—'}</TableCell>
                                            <TableCell><StatusChip row={r} /></TableCell>
                                            <TableCell>
                                                <Typography variant="body2" color="text.secondary">{notes.join(' · ')}</Typography>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
};

export default PushQueuePage;
